/* ScientificCalculator — floating, draggable, RESIZABLE, keyboard-friendly
 * widget for the whiteboard and live classroom.
 *
 * v2 upgrades (per user feedback):
 *  • Bigger default layout (420 × 620) with roomy square buttons.
 *  • Colourful per-key palette — numbers stay neutral, operators go amber,
 *    scientific keys cyan, primary "=" bright yellow, danger red.
 *  • Click sound on every press (Web Audio API — no asset load).
 *  • Glow + press-scale animation on button press.
 *  • Corner resize handle (bottom-right) so teachers can drag to a
 *    comfortable size; min 320×480, max 640×860.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { X, GripVertical } from "lucide-react";

const DEG_FUNCS = ["sin", "cos", "tan"];
const RAD_FUNCS = ["asin", "acos", "atan"];

// ─── Expression evaluator ────────────────────────────────────────────────
function toJs(expr, ans) {
  let s = expr;
  s = s.replace(/π/g, "Math.PI");
  s = s.replace(/\bpi\b/g, "Math.PI");
  s = s.replace(/(?<![A-Za-z0-9_])e(?![A-Za-z0-9_])/g, "Math.E");
  s = s.replace(/\bans\b/g, `(${ans || 0})`);
  s = s.replace(/\^/g, "**");
  s = s.replace(/(\d+(?:\.\d+)?)%/g, "($1/100)");
  DEG_FUNCS.forEach((fn) => {
    const re = new RegExp(`\\b${fn}\\(`, "g");
    s = s.replace(re, `Math.${fn}((Math.PI/180)*`);
  });
  RAD_FUNCS.forEach((fn) => {
    const re = new RegExp(`\\b${fn}\\(`, "g");
    s = s.replace(re, `((180/Math.PI)*Math.${fn}(`);
  });
  s = s.replace(/\bln\(/g, "Math.log(");
  s = s.replace(/\blog\(/g, "Math.log10(");
  s = s.replace(/\bsqrt\(/g, "Math.sqrt(");
  s = s.replace(/\bexp\(/g, "Math.exp(");
  s = s.replace(/\babs\(/g, "Math.abs(");
  const opens = (s.match(/\(/g) || []).length;
  const closes = (s.match(/\)/g) || []).length;
  if (opens > closes) s = s + ")".repeat(opens - closes);
  return s;
}

function evaluate(expr, ans) {
  if (!expr.trim()) return "";
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function("return (" + toJs(expr, ans) + ")");
    const v = fn();
    if (typeof v !== "number" || !isFinite(v)) return "";
    return Math.abs(v) > 1e12 ? v.toExponential(6) : parseFloat(v.toPrecision(12)).toString();
  } catch {
    return "";
  }
}

// ─── Click sound (Web Audio — no asset load) ─────────────────────────────
let _audioCtx = null;
function playClick() {
  try {
    if (!_audioCtx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      _audioCtx = new AC();
    }
    const ctx = _audioCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.05);
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.09);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  } catch { /* silent */ }
}

// ─── Component ────────────────────────────────────────────────────────────
export default function ScientificCalculator({ onClose, defaultPosition }) {
  const [pos, setPos] = useState(defaultPosition || { x: 40, y: 90 });
  const [size, setSize] = useState({ w: 420, h: 620 });
  const [expr, setExpr] = useState("");
  const [ans, setAns] = useState(0);
  const [history, setHistory] = useState([]);
  const [second, setSecond] = useState(false);
  const [pressedKey, setPressedKey] = useState(null); // key index for glow flash
  const inputRef = useRef(null);
  const dragStartRef = useRef(null);
  const resizeStartRef = useRef(null);

  const preview = evaluate(expr, ans);
  useEffect(() => { inputRef.current?.focus(); }, []);

  // ── Actions ──
  const flash = (i) => {
    setPressedKey(i);
    setTimeout(() => setPressedKey((cur) => (cur === i ? null : cur)), 220);
  };
  const insert = useCallback((s) => {
    setExpr((cur) => cur + s);
    inputRef.current?.focus();
  }, []);
  const backspace = () => setExpr((cur) => cur.slice(0, -1));
  const clear = () => setExpr("");
  const equals = () => {
    const v = evaluate(expr, ans);
    if (v === "") return;
    setHistory((h) => [{ expr, value: v }, ...h].slice(0, 20));
    setAns(parseFloat(v));
    setExpr(v);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter") { e.preventDefault(); equals(); }
      if (e.key === "Escape") { onClose?.(); }
    };
    const node = inputRef.current;
    node?.addEventListener("keydown", onKey);
    return () => node?.removeEventListener("keydown", onKey);
  }, [expr, ans]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Drag (title bar) ──
  const onDragStart = (e) => {
    dragStartRef.current = { startX: e.clientX, startY: e.clientY, posX: pos.x, posY: pos.y };
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
  };
  const onDragMove = (e) => {
    const s = dragStartRef.current;
    if (!s) return;
    setPos({
      x: Math.max(0, Math.min(window.innerWidth - 200, s.posX + (e.clientX - s.startX))),
      y: Math.max(0, Math.min(window.innerHeight - 120, s.posY + (e.clientY - s.startY))),
    });
  };
  const onDragEnd = () => {
    dragStartRef.current = null;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
  };

  // ── Resize handle (bottom-right corner) ──
  const onResizeStart = (e) => {
    e.stopPropagation();
    resizeStartRef.current = { startX: e.clientX, startY: e.clientY, w: size.w, h: size.h };
    window.addEventListener("mousemove", onResizeMove);
    window.addEventListener("mouseup", onResizeEnd);
  };
  const onResizeMove = (e) => {
    const s = resizeStartRef.current;
    if (!s) return;
    setSize({
      w: Math.max(320, Math.min(640, s.w + (e.clientX - s.startX))),
      h: Math.max(480, Math.min(860, s.h + (e.clientY - s.startY))),
    });
  };
  const onResizeEnd = () => {
    resizeStartRef.current = null;
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", onResizeEnd);
  };

  // ── Key layout ──
  const KEYS = [
    [
      { l: second ? "sin⁻¹" : "sin", v: second ? "asin(" : "sin(", tint: "sci" },
      { l: second ? "cos⁻¹" : "cos", v: second ? "acos(" : "cos(", tint: "sci" },
      { l: second ? "tan⁻¹" : "tan", v: second ? "atan(" : "tan(", tint: "sci" },
      { l: "2nd", v: null, action: () => setSecond((s) => !s), tint: second ? "sciOn" : "sci" },
      { l: "π", v: "π", tint: "sci" },
    ],
    [
      { l: "log", v: "log(", tint: "sci" },
      { l: "ln", v: "ln(", tint: "sci" },
      { l: "√", v: "sqrt(", tint: "sci" },
      { l: "x²", v: "^2", tint: "sci" },
      { l: "xʸ", v: "^", tint: "sci" },
    ],
    [
      { l: "(", v: "(", tint: "op" },
      { l: ")", v: ")", tint: "op" },
      { l: "%", v: "%", tint: "op" },
      { l: "1/x", v: "1/", tint: "sci" },
      { l: "ans", v: "ans", tint: "sci" },
    ],
    [
      { l: "7", v: "7", tint: "num" },
      { l: "8", v: "8", tint: "num" },
      { l: "9", v: "9", tint: "num" },
      { l: "÷", v: "/", tint: "op" },
      { l: "C", v: null, action: clear, tint: "danger" },
    ],
    [
      { l: "4", v: "4", tint: "num" },
      { l: "5", v: "5", tint: "num" },
      { l: "6", v: "6", tint: "num" },
      { l: "×", v: "*", tint: "op" },
      { l: "⌫", v: null, action: backspace, tint: "op" },
    ],
    [
      { l: "1", v: "1", tint: "num" },
      { l: "2", v: "2", tint: "num" },
      { l: "3", v: "3", tint: "num" },
      { l: "−", v: "-", tint: "op" },
      { l: "=", v: null, action: equals, tint: "primary" },
    ],
    [
      { l: "±", v: null, action: () => setExpr((c) => (c.startsWith("-") ? c.slice(1) : "-" + c)), tint: "num" },
      { l: "0", v: "0", tint: "num" },
      { l: ".", v: ".", tint: "num" },
      { l: "+", v: "+", tint: "op" },
      { l: "", v: "", tint: "spacer" },
    ],
  ];

  // ── Palette (dark theme, big contrast) ──
  const palette = {
    num:      { bg: "#1F2937", bgHover: "#2A3548", color: "#F9FAFB", border: "rgba(255,255,255,0.10)", glow: "rgba(96,165,250,0.85)" },
    op:       { bg: "#F59E0B", bgHover: "#FBBF24", color: "#0A0C10", border: "rgba(255,255,255,0.10)", glow: "rgba(251,191,36,0.85)" },
    sci:      { bg: "#164E63", bgHover: "#1E6E88", color: "#7DD3FC", border: "rgba(125,211,252,0.28)", glow: "rgba(125,211,252,0.85)" },
    sciOn:    { bg: "#22D3EE", bgHover: "#22D3EE", color: "#0A0C10", border: "#22D3EE", glow: "rgba(34,211,238,0.95)" },
    primary:  { bg: "#FFD600", bgHover: "#FFE04A", color: "#0A0C10", border: "#FFD600", glow: "rgba(255,214,0,0.95)" },
    danger:   { bg: "#7F1D1D", bgHover: "#991B1B", color: "#FCA5A5", border: "rgba(252,165,165,0.28)", glow: "rgba(248,113,113,0.85)" },
    spacer:   { bg: "transparent", bgHover: "transparent", color: "transparent", border: "transparent", glow: "transparent" },
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
      className="fixed z-[85] bg-gradient-to-br from-[#111827] via-[#0A0C10] to-[#111827] border border-white/15 rounded-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] select-none flex flex-col overflow-hidden"
      data-testid="scientific-calculator"
    >
      {/* Drag handle */}
      <div
        onMouseDown={onDragStart}
        className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between cursor-move bg-gradient-to-r from-[#1F2937] via-[#111827] to-[#1F2937]"
        data-testid="calc-drag-handle"
      >
        <div className="flex items-center gap-2 text-white/70">
          <GripVertical size={13} />
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] font-bold">
            Scientific · deg{second ? " · 2nd" : ""}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="calc-close"
          className="w-7 h-7 rounded-md hover:bg-white/15 flex items-center justify-center text-white/70"
        >
          <X size={14} />
        </button>
      </div>

      {/* Display */}
      <div className="p-4 bg-[#0F1218] border-b border-white/10">
        <input
          ref={inputRef}
          value={expr}
          onChange={(e) => setExpr(e.target.value)}
          placeholder="0"
          data-testid="calc-display-input"
          className="w-full bg-transparent border-none outline-none text-white text-right text-3xl font-mono tabular-nums"
        />
        <div className="text-right text-sm text-[#FFD600]/80 font-mono h-5 mt-1">
          {preview && preview !== expr ? `= ${preview}` : "\u00a0"}
        </div>
      </div>

      {/* Keypad */}
      <div className="p-3 grid grid-cols-5 gap-2 flex-1" data-testid="calc-keypad">
        {KEYS.flat().map((k, i) => {
          if (k.tint === "spacer") return <div key={i} />;
          const p = palette[k.tint] || palette.num;
          const pressed = pressedKey === i;
          return (
            <button
              key={i}
              onClick={() => {
                playClick();
                flash(i);
                if (k.action) k.action(); else insert(k.v);
              }}
              data-testid={`calc-key-${k.l.replace(/[^a-zA-Z0-9]/g, "_") || `x${i}`}`}
              className="rounded-lg font-bold text-lg transition-all active:scale-95 flex items-center justify-center"
              style={{
                background: pressed ? p.bgHover : p.bg,
                color: p.color,
                border: `1px solid ${p.border}`,
                boxShadow: pressed
                  ? `0 0 22px ${p.glow}, inset 0 0 12px ${p.glow}`
                  : `0 2px 0 rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)`,
                transform: pressed ? "translateY(1px) scale(0.97)" : "translateY(0) scale(1)",
              }}
              onMouseEnter={(e) => { if (!pressed) e.currentTarget.style.background = p.bgHover; }}
              onMouseLeave={(e) => { if (!pressed) e.currentTarget.style.background = p.bg; }}
            >
              {k.l}
            </button>
          );
        })}
      </div>

      {history.length > 0 && (
        <div className="border-t border-white/10 max-h-28 overflow-y-auto text-[12px] font-mono text-white/55 px-3 py-2">
          {history.map((h, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5">
              <span className="opacity-60 truncate flex-1">{h.expr}</span>
              <span className="text-[#FFD600] font-semibold">= {h.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Resize handle (bottom-right corner) */}
      <div
        onMouseDown={onResizeStart}
        data-testid="calc-resize-handle"
        title="Drag to resize"
        className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize"
        style={{
          background: "linear-gradient(135deg, transparent 0%, transparent 45%, rgba(255,255,255,0.35) 45%, rgba(255,255,255,0.35) 55%, transparent 55%, transparent 68%, rgba(255,255,255,0.25) 68%, rgba(255,255,255,0.25) 78%, transparent 78%)",
        }}
      />
    </motion.div>
  );
}
